import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { usePageTitle } from '../../lib/hooks/usePageTitle.js';
import { api } from '../../lib/api.js';
import { formatCurrency, formatDate } from '../../lib/formatters.js';
import { useDemoMode } from '../../context/DemoModeContext.js';

interface ClaimRow {
  id: string;
  claimNumber: string;
  claimType: string;
  status: string;
  claimedAmount: string;
  isoCurrencyCode: string;
  verificationConfidence?: string | null;
  periodStart: string;
  periodEnd: string;
  submittedAt?: string | null;
  createdAt: string;
  org?: { name: string } | null;
}

// For brand/approver roles — internal statuses
const STATUS_COLORS: Record<string, string> = {
  DRAFT: 'bg-slate-100 text-slate-600',
  SUBMITTED: 'bg-copper-soft text-copper-deep',
  AI_PROCESSING: 'bg-copper-soft text-copper',
  AWAITING_REVIEW: 'bg-copper-soft text-copper-deep',
  UNDER_REVIEW: 'bg-copper-soft text-copper-deep',
  AUTO_APPROVED: 'bg-success/10 text-success',
  APPROVED: 'bg-success/10 text-success',
  PARTIALLY_APPROVED: 'bg-success/10 text-success',
  REJECTED: 'bg-danger/10 text-danger',
  PAYMENT_QUEUED: 'bg-success/10 text-success',
  PAID: 'bg-success/20 text-success',
  DISPUTED: 'bg-danger/10 text-danger',
  CANCELLED: 'bg-slate-100 text-slate-500',
};

// For distributor role — public statuses only
const PUBLIC_STATUS_COLORS: Record<string, string> = {
  DRAFT: 'bg-slate-100 text-slate-600',
  PENDING_REVIEW: 'bg-copper-soft text-copper-deep',
  APPROVED: 'bg-success/10 text-success',
  PARTIALLY_APPROVED: 'bg-success/10 text-success',
  REJECTED: 'bg-danger/10 text-danger',
  PAID: 'bg-success/20 text-success',
  ADDITIONAL_INFO_NEEDED: 'bg-copper/20 text-copper-deep',
};

export function ClaimsListPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { persona } = useDemoMode();
  const isDistributor = persona.role === 'distributor_submitter';
  usePageTitle('claims.list.title');
  const [claims, setClaims] = useState<ClaimRow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get<ClaimRow[]>('/claims')
      .then(setClaims)
      .catch(() => setClaims([]))
      .finally(() => setLoading(false));
  }, []);

  const colorMap = isDistributor ? PUBLIC_STATUS_COLORS : STATUS_COLORS;
  const statusI18nNs = isDistributor ? 'claims.publicStatus' : 'wizard.status';

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-serif italic text-3xl text-ink">{t('claims.title')}</h1>
        {isDistributor && (
          <button
            data-tour="new-claim-button"
            type="button"
            onClick={() => navigate('/submit/new')}
            className="px-4 py-2 bg-copper text-bone-soft rounded text-sm font-semibold hover:bg-copper-warm transition-colors"
          >
            + {t('wizard.actions.newClaim')}
          </button>
        )}
      </div>

      {loading ? (
        <p className="text-slate-400 text-sm">Loading...</p>
      ) : claims.length === 0 ? (
        <div className="text-center py-16 border border-hairline rounded bg-bone">
          <p className="text-slate-500">{t('claims.empty')}</p>
          {isDistributor && (
            <button
              type="button"
              onClick={() => navigate('/submit/new')}
              className="mt-4 px-4 py-2 bg-copper text-bone-soft rounded text-sm font-semibold hover:bg-copper-warm transition-colors"
            >
              + {t('wizard.actions.newClaim')}
            </button>
          )}
        </div>
      ) : (
        <div data-tour="claims-list" className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-hairline text-xs uppercase tracking-wide text-slate-500">
                <th className="text-left pb-2 pr-4">{t('claims.claimNumber')}</th>
                <th className="text-left pb-2 pr-4">{t('claims.type')}</th>
                <th className="text-left pb-2 pr-4">{t('claims.status')}</th>
                <th className="text-right pb-2 pr-4">{t('claims.amount')}</th>
                <th className="text-left pb-2 pr-4">{t('claims.period')}</th>
                <th className="text-left pb-2">{t('claims.submittedAt')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-hairline">
              {claims.map((c) => (
                <tr
                  key={c.id}
                  onClick={() => navigate(`/claims/${c.id}`)}
                  className="hover:bg-bone cursor-pointer transition-colors"
                >
                  <td className="py-3 pr-4 font-mono text-xs text-slate-600">{c.claimNumber}</td>
                  <td className="py-3 pr-4">
                    {t(`wizard.claimTypes.${c.claimType}.label`, { defaultValue: c.claimType })}
                  </td>
                  <td className="py-3 pr-4">
                    <span className={['text-xs font-semibold px-2 py-0.5 rounded-sm', colorMap[c.status] ?? 'bg-slate-100 text-slate-600'].join(' ')}>
                      {t(`${statusI18nNs}.${c.status}`, { defaultValue: c.status })}
                    </span>
                  </td>
                  <td className="py-3 pr-4 text-right font-mono">
                    {formatCurrency(Number(c.claimedAmount), c.isoCurrencyCode)}
                  </td>
                  <td className="py-3 pr-4 text-slate-500 text-xs">
                    {formatDate(c.periodStart, 'short')} — {formatDate(c.periodEnd, 'short')}
                  </td>
                  <td className="py-3 text-slate-400 text-xs">
                    {c.submittedAt ? formatDate(c.submittedAt) : formatDate(c.createdAt)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
